import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TrendingUp, TrendingDown, Minus, RotateCcw, ArrowUpRight } from "lucide-react";

interface Props {
  cycle: string;
  cycleHistory?: string[];
}

const CYCLE_CONFIG: Record<string, {
  label: string;
  description: string;
  color: string;
  bgColor: string;
  Icon: typeof TrendingUp;
  emoji: string;
}> = {
  expansion: {
    label: "Expansion",
    description: "Property values rising, strong demand, low vacancy. Be cautious of overpaying.",
    color: "text-emerald-600",
    bgColor: "bg-emerald-500/10",
    Icon: TrendingUp,
    emoji: "📈",
  },
  stable: {
    label: "Stable",
    description: "Balanced market with predictable returns. Good for building fundamentals.",
    color: "text-primary",
    bgColor: "bg-primary/10",
    Icon: Minus,
    emoji: "⚖️",
  },
  slowdown: {
    label: "Slowdown",
    description: "Growth decelerating. Rent growth may stall. Focus on reserves.",
    color: "text-amber-600",
    bgColor: "bg-amber-500/10",
    Icon: TrendingDown,
    emoji: "📉",
  },
  downturn: {
    label: "Downturn",
    description: "Values may drop, vacancies increase. Strong reserves are critical.",
    color: "text-destructive",
    bgColor: "bg-destructive/10",
    Icon: TrendingDown,
    emoji: "🔻",
  },
  recovery: {
    label: "Recovery",
    description: "Market rebounding. Opportunities emerge for patient investors.",
    color: "text-accent",
    bgColor: "bg-accent/10",
    Icon: RotateCcw,
    emoji: "🔄",
  },
};

export default function MarketIndicator({ cycle, cycleHistory }: Props) {
  const config = CYCLE_CONFIG[cycle] || CYCLE_CONFIG.stable;
  const Icon = config.Icon;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={`flex items-center gap-2 ${config.bgColor} rounded-lg px-3 py-1.5 cursor-help transition-all`}>
          <span className="text-sm">{config.emoji}</span>
          <Icon className={`w-3.5 h-3.5 ${config.color}`} />
          <span className={`text-xs font-semibold ${config.color}`}>{config.label}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <p className="text-xs font-semibold mb-1">{config.label} Market</p>
        <p className="text-xs text-muted-foreground">{config.description}</p>
        {cycleHistory && cycleHistory.length > 1 && (
          <div className="flex gap-1 mt-2">
            {cycleHistory.slice(-6).map((c, i) => {
              const cc = CYCLE_CONFIG[c] || CYCLE_CONFIG.stable;
              return <span key={i} className="text-[10px]" title={cc.label}>{cc.emoji}</span>;
            })}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
